import merge from 'lodash/merge';
import { myProgress as defaultState } from './default.json';
import { actionTypes } from '../actions/my-progress';
import { flattenLesson, flattenCourses, flattenPaths } from '../utils/my-progress-utils';

function updateState(state, update) {
  const updatedState = {};
  ['paths', 'playlists', 'courses', 'lessons'].forEach(type => {
    if (update[type]) {
      updatedState[type] = merge({}, state[type], update[type]);
    } else {
      updatedState[type] = state[type] || {};
    }
  });
  return {
    ...state,
    error: null,
    inProgress: false,
    ...updatedState,
  };
}

const handlers = {
  [actionTypes.PROGRESS_GET]: (state, action) => {
    let update = null;
    if (action.progress.paths) {
      update = { ...update, ...flattenPaths(action.progress, state) };
    } else if (action.progress.courses) {
      update = { ...update, ...flattenCourses(action.progress) };
    } else if (action.progress.lessons) {
      update = { ...update, ...flattenLesson(action.progress) };
    }
    return update ? updateState(state, update) : state;
  },
  [actionTypes.PROGRESS_GET_LEVELS_COMPLETED]: (state, action) => ({
    ...state,
    error: null,
    inProgress: false,
    completed: {
      ...(action.completed || []),
    },
  }),
  [actionTypes.PROGRESS_GET_COUNTS]: (state, action) => ({
    ...state,
    error: null,
    inProgress: false,
    counts: {
      ...(action.counts || {}),
    },
  }),
  [actionTypes.PROGRESS_GET_RECENT]: (state, action) => ({
    ...state,
    error: null,
    inProgress: false,
    recent: {
      ...(action.recent || {}),
    },
  }),
  [actionTypes.PROGRESS_GET_RECENTLY_VIEWED]: (state, action) => ({
    ...state,
    error: null,
    inProgress: false,
    recentlyViewed: {
      ...(action.recentlyViewed || {}),
    },
  }),
  [actionTypes.PATHS_PROGRESS_UPDATED]: (state, action) => {
    const { pathID, playlistID, courseID, lessonID, style, instrument, courseType } = action;

    const update = {
      currentPath: pathID,
      paths: {
        [pathID]: {
          style,
          instrument,
          currentPlaylist: playlistID,
        },
      },
      playlists: {
        [playlistID]: {
          composite: [pathID],
          currentCourse: courseID,
        },
      },
      courses: {
        [courseID]: {
          type: courseType,
          composite: [pathID, playlistID],
          currentLesson: lessonID,
        },
      },
      lessons: {
        [lessonID]: { composite: [pathID, playlistID, courseID] },
      },
    };
    return updateState(state, update);
  },
  [actionTypes.COURSES_PROGRESS_UPDATED]: (state, action) => {
    const { courseID, lessonID, type } = action;
    const update = {
      courses: {
        [courseID]: {
          type,
          currentLesson: lessonID,
        },
      },
    };
    return updateState(state, update);
  },
  [actionTypes.STAND_ALONE_LESSON_PROGRESS_UPDATE]: (state, action) => {
    const { lessonID, lessonType } = action || {};
    const update = {
      lessons: {
        [lessonID]: {
          updatedAt: new Date(),
          type: lessonType,
        },
      },
    };
    return updateState(state, update);
  },
  [actionTypes.LESSONS_PROGRESS_UPDATED]: (state, action) => {
    const { lessonID, lessonType, videoWatchedSeconds, completed } = action;
    const update = {
      lessons: {
        [lessonID]: {
          updatedAt: new Date(),
          type: lessonType,
          videoWatchedSeconds,
          completed,
        },
      },
    };
    return updateState(state, update);
  },
  [actionTypes.FC_SIGNOUT]: () => defaultState,
  [actionTypes.PROGRESS_GET_ERROR]: (state, action) => ({
    ...state,
    error: action.error.message,
    inProgress: false,
  }),
  [actionTypes.PROGRESS_IN_PROGRESS]: state => ({
    ...state,
    error: null,
    inProgress: true,
  }),
  [actionTypes.COMPLETED_LESSONS_UPDATED]: (state, action) => {
    const lessons = {};
    action.options.forEach(lesson => {
      const lessonID = lesson['lesson-id'];
      const lessonType = lesson['lesson-type'];
      const { completed } = lesson;
      lessons[lessonID] = {
        updatedAt: new Date(),
        type: lessonType,
        completed,
      };
    });
    const update = { lessons };
    return updateState(state, update);
  },
  [actionTypes.SET_MARK_COMPLETE_MODAL]: (state, action) => ({
    ...state,
    markCompleteModalData: action.options,
  }),
  [actionTypes.SET_LEVEL]: (state, { level }) => ({
    ...state,
    level,
  }),
};

export default (state = defaultState, action) => {
  const handler = handlers[action.type];
  return handler ? handler(state, action) : state;
};
